package com.vizio.redwolf.pay.client

import com.vizio.redwolf.pay.model.CreateOrderInfo
import com.vizio.redwolf.pay.model.CreateTransactionInfo
import com.vizio.redwolf.pay.model.OrderPreviewInfo
import com.vizio.redwolf.pay.model.PartnerPlansInfo
import com.vizio.redwolf.pay.model.PreviewSubscriptionInfo
import com.vizio.redwolf.pay.model.PurchaseSubscriptionInfo
import com.vizio.redwolf.utils.AppConfig
import com.vizio.redwolf.utils.CONTENT_TYPE_APP_JSON_UTF8
import com.vizio.redwolf.utils.ClientConfig
import com.vizio.redwolf.utils.HttpResult
import com.vizio.redwolf.utils.PartnerEnvironment
import com.vizio.redwolf.utils.PayEnvironment
import com.vizio.redwolf.utils.SUBSCRIPTION_API_KEY
import com.vizio.redwolf.utils.SignatureAuthInfo
import com.vizio.redwolf.utils.applyAppConfig
import com.vizio.redwolf.utils.applyTimeout
import com.vizio.redwolf.utils.applyUserBearerToken
import com.vizio.redwolf.utils.safeHttpCall
import com.vizio.redwolf.utils.setSentryHeader
import com.vizio.redwolf.utils.setTvAppId
import io.ktor.client.request.HttpRequestBuilder
import io.ktor.client.request.delete
import io.ktor.client.request.get
import io.ktor.client.request.header
import io.ktor.client.request.headers
import io.ktor.client.request.parameter
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody
import io.ktor.client.statement.HttpResponse
import io.ktor.http.HttpHeaders
import io.ktor.http.URLProtocol
import io.ktor.http.path
import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.encodeToJsonElement

class SubscriptionHttpClient : PayApiHttpClient {

    private val host: String
    private val appConfig: AppConfig
    private val subsPath: String
    private val subsApiKey: String?
    private val timeout: Long
    private val isPartner: Boolean

    constructor(payEnv: PayEnvironment, appConfig: AppConfig, clientConfig: ClientConfig) : super(clientConfig) {
        host = payEnv.payHost
        this.appConfig = appConfig
        subsPath = payEnv.subscriptionsPath
        subsApiKey = payEnv.vizioApiSubscriptionKey
        this.timeout = clientConfig.timeout
        isPartner = false
    }

    constructor(partnerEnvironment: PartnerEnvironment, appConfig: AppConfig, clientConfig: ClientConfig) : super(clientConfig) {
        host = partnerEnvironment.host
        this.appConfig = appConfig
        subsPath = partnerEnvironment.subscriptionsPath
        subsApiKey = null
        this.timeout = clientConfig.timeout
        isPartner = true
    }

    private val json = Json { encodeDefaults = true }

    suspend fun getSubscriptions(authToken: String, sentryHeader: String?, tvAppId: String?, signatureAuthInfo: SignatureAuthInfo? = null): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = this@SubscriptionHttpClient.host
                    path("$subsPath/accounts/subscriptions")
                }
                applyUserBearerToken(authToken)
                setApiKeyHeader()
                setSentryHeader(sentryHeader)
                setTvAppId(tvAppId)
                applyAppConfig(appConfig)
                applyTimeout(timeout)
                signatureAuthInfo?.setHeaders(this)
            }
        }
    }

    suspend fun getSubscriptionsWithPlanCode(authToken: String, planCode: String, sentryHeader: String?, tvAppId: String?, signatureAuthInfo: SignatureAuthInfo? = null): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = this@SubscriptionHttpClient.host
                    path("$subsPath/accounts/subscriptions/plans/$planCode")
                }
                applyUserBearerToken(authToken)
                setApiKeyHeader()
                setSentryHeader(sentryHeader)
                setTvAppId(tvAppId)
                applyAppConfig(appConfig)
                applyTimeout(timeout)
                signatureAuthInfo?.setHeaders(this)
            }
        }
    }

    suspend fun reactivateSubscription(authToken: String, subscriptionId: String, sentryHeader: String?, tvAppId: String?, signatureAuthInfo: SignatureAuthInfo? = null): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.put {
                url {
                    protocol = URLProtocol.HTTPS
                    host = this@SubscriptionHttpClient.host
                    path("$subsPath/accounts/subscriptions/$subscriptionId")
                }
                applyUserBearerToken(authToken)
                setApiKeyHeader()
                setSentryHeader(sentryHeader)
                setTvAppId(tvAppId)
                applyAppConfig(appConfig)
                applyTimeout(timeout)
                signatureAuthInfo?.setHeaders(this)
            }
        }
    }

    suspend fun cancelSubscription(authToken: String, subscriptionId: String, sentryHeader: String?, tvAppId: String?, signatureAuthInfo: SignatureAuthInfo? = null): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.delete {
                url {
                    protocol = URLProtocol.HTTPS
                    host = this@SubscriptionHttpClient.host
                    path("$subsPath/accounts/subscriptions/$subscriptionId")
                }
                applyUserBearerToken(authToken)
                setApiKeyHeader()
                setSentryHeader(sentryHeader)
                setTvAppId(tvAppId)
                applyAppConfig(appConfig)
                applyTimeout(timeout)
                signatureAuthInfo?.setHeaders(this)
            }
        }
    }

    suspend fun terminateSubscription(authToken: String, subscriptionId: String, sentryHeader: String?, tvAppId: String?, signatureAuthInfo: SignatureAuthInfo? = null): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.delete {
                url {
                    protocol = URLProtocol.HTTPS
                    host = this@SubscriptionHttpClient.host
                    path("$subsPath/accounts/subscriptions/$subscriptionId/terminate")
                }
                applyUserBearerToken(authToken)
                setApiKeyHeader()
                setSentryHeader(sentryHeader)
                setTvAppId(tvAppId)
                applyAppConfig(appConfig)
                applyTimeout(timeout)
                signatureAuthInfo?.setHeaders(this)
            }
        }
    }

    suspend fun previewSubscription(authToken: String, previewInfo: PreviewSubscriptionInfo, sentryHeader: String?, tvAppId: String?, signatureAuthInfo: SignatureAuthInfo? = null): HttpResult<HttpResponse> {
        val previewRequestBody = Json.encodeToJsonElement(previewInfo)
        return safeHttpCall {
            makePreviewApiCall(
                authToken = authToken,
                previewRequestBody = previewRequestBody,
                sentryHeader = sentryHeader,
                tvAppId = tvAppId,
                signatureAuthInfo = signatureAuthInfo
            )
        }
    }
    private suspend fun makePreviewApiCall(authToken: String, previewRequestBody: JsonElement?, sentryHeader: String?, tvAppId: String?, signatureAuthInfo: SignatureAuthInfo? = null): HttpResponse {
        return httpClient.post {
            url {
                protocol = URLProtocol.HTTPS
                host = this@SubscriptionHttpClient.host
                path("$subsPath/accounts/subscriptions/preview")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
            }
            applyUserBearerToken(authToken)
            setApiKeyHeader()
            setSentryHeader(sentryHeader)
            setTvAppId(tvAppId)
            signatureAuthInfo?.setHeaders(this)
            setBody(previewRequestBody)
            applyAppConfig(appConfig)
            applyTimeout(timeout)
        }
    }

    suspend fun purchaseSubscription(authToken: String, purchaseInfo: PurchaseSubscriptionInfo, sentryHeader: String?, tvAppId: String?, signatureAuthInfo: SignatureAuthInfo? = null): HttpResult<HttpResponse> {
        val purchaseRequestBody = json.encodeToJsonElement(purchaseInfo)
        return safeHttpCall {
            makePurchaseApiCall(
                authToken = authToken,
                purchaseRequestBody = purchaseRequestBody,
                sentryHeader = sentryHeader,
                tvAppId = tvAppId,
                signatureAuthInfo = signatureAuthInfo
            )
        }
    }

    private suspend fun makePurchaseApiCall(authToken: String, purchaseRequestBody: JsonElement?, sentryHeader: String?, tvAppId: String?, signatureAuthInfo: SignatureAuthInfo? = null): HttpResponse {
        return httpClient.post {
            url {
                protocol = URLProtocol.HTTPS
                host = this@SubscriptionHttpClient.host
                path("$subsPath/accounts/subscriptions")
            }
            headers {
                append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
            }
            applyUserBearerToken(authToken)
            setApiKeyHeader()
            setSentryHeader(sentryHeader)
            setTvAppId(tvAppId)
            signatureAuthInfo?.setHeaders(this)
            setBody(purchaseRequestBody)
            applyAppConfig(appConfig)
            applyTimeout(timeout)
        }
    }
    suspend fun createTransaction(authToken: String, transactionInfo: CreateTransactionInfo, sentryHeader: String?, tvAppId: String?): HttpResult<HttpResponse> {
        val transactionRequestBody = json.encodeToJsonElement(transactionInfo)
        return safeHttpCall {
            httpClient.post {
                url {
                    protocol = URLProtocol.HTTPS
                    host = this@SubscriptionHttpClient.host
                    path("/accounts/orders")
                }
                headers {
                    append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                }
                applyUserBearerToken(authToken)
                setSentryHeader(sentryHeader)
                setApiKeyHeader()
                setTvAppId(tvAppId)
                setBody(transactionRequestBody)
                applyAppConfig(appConfig)
                applyTimeout(timeout)
            }
        }
    }
    suspend fun createOrder(authToken: String, createOrderInfo: CreateOrderInfo, sentryHeader: String?, tvAppId: String?, signatureAuthInfo: SignatureAuthInfo? = null): HttpResult<HttpResponse> {
        val createOrderRequestBody = Json.encodeToJsonElement(createOrderInfo)
        return safeHttpCall {
            httpClient.post {
                url {
                    protocol = URLProtocol.HTTPS
                    host = this@SubscriptionHttpClient.host
                    path("$subsPath/accounts/orders")
                }
                headers {
                    append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                }
                applyUserBearerToken(authToken)
                setSentryHeader(sentryHeader)
                setApiKeyHeader()
                setTvAppId(tvAppId)
                setBody(createOrderRequestBody)
                applyAppConfig(appConfig)
                applyTimeout(timeout)
                signatureAuthInfo?.setHeaders(this)
            }
        }
    }
    suspend fun orderPreview(authToken: String, orderPreviewInfo: OrderPreviewInfo, sentryHeader: String?, tvAppId: String?, signatureAuthInfo: SignatureAuthInfo? = null): HttpResult<HttpResponse> {
        val orderPreviewRequestBody = Json.encodeToJsonElement(orderPreviewInfo)
        return safeHttpCall {
            httpClient.post {
                url {
                    protocol = URLProtocol.HTTPS
                    host = this@SubscriptionHttpClient.host
                    path("$subsPath/accounts/orders/preview")
                }
                headers {
                    append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                }
                applyUserBearerToken(authToken)
                setSentryHeader(sentryHeader)
                setApiKeyHeader()
                setTvAppId(tvAppId)
                setBody(orderPreviewRequestBody)
                applyAppConfig(appConfig)
                applyTimeout(timeout)
                signatureAuthInfo?.setHeaders(this)
            }
        }
    }
    suspend fun getV2Invoices(authToken: String, pageNumber: Int?, pageSize: Int?, sentryHeader: String?, tvAppId: String?, signatureAuthInfo: SignatureAuthInfo? = null): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = this@SubscriptionHttpClient.host
                    path("$subsPath/v2/accounts/invoices")
                    pageNumber?.let { parameter("pageNumber", it) }
                    pageSize?.let { parameter("pageSize", it) }
                }
                applyUserBearerToken(authToken)
                setSentryHeader(sentryHeader)
                setApiKeyHeader()
                setTvAppId(tvAppId)
                applyAppConfig(appConfig)
                applyTimeout(timeout)
                signatureAuthInfo?.setHeaders(this)
            }
        }
    }
    suspend fun getV2InvoiceById(authToken: String, invoiceId: String, sentryHeader: String?, tvAppId: String?, signatureAuthInfo: SignatureAuthInfo? = null): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = this@SubscriptionHttpClient.host
                    path("$subsPath/v2/accounts/invoices/$invoiceId")
                }
                applyUserBearerToken(authToken)
                setSentryHeader(sentryHeader)
                setApiKeyHeader()
                setTvAppId(tvAppId)
                applyAppConfig(appConfig)
                applyTimeout(timeout)
                signatureAuthInfo?.setHeaders(this)
            }
        }
    }

    suspend fun getAccountInvoiceSummaries(authToken: String, year: Int?, month: Int?, sentryHeader: String?, tvAppId: String?): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = this@SubscriptionHttpClient.host
                    path("$subsPath/accounts/invoices")
                    parameter("year", year)
                    parameter("month", month)
                }
                applyUserBearerToken(authToken)
                setSentryHeader(sentryHeader)
                setApiKeyHeader()
                setTvAppId(tvAppId)
                applyAppConfig(appConfig)
                applyTimeout(timeout)
            }
        }
    }
    suspend fun getAccountInvoice(authToken: String, invoiceId: String, sentryHeader: String?, tvAppId: String?): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = this@SubscriptionHttpClient.host
                    path("$subsPath/accounts/invoices/$invoiceId")
                }
                applyUserBearerToken(authToken)
                setSentryHeader(sentryHeader)
                setApiKeyHeader()
                setTvAppId(tvAppId)
                applyAppConfig(appConfig)
                applyTimeout(timeout)
            }
        }
    }
    suspend fun getPartnerIdPlans(partnerPlansInfo: PartnerPlansInfo, sentryHeader: String?, tvAppId: String?): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = this@SubscriptionHttpClient.host
                    path("$subsPath/plans/partners/${partnerPlansInfo.partnerId}")
                }
                headers {
                    append(HttpHeaders.ContentType, CONTENT_TYPE_APP_JSON_UTF8)
                }
                setApiKeyHeader()
                setSentryHeader(sentryHeader)
                setTvAppId(tvAppId)
                applyAppConfig(appConfig)
                applyTimeout(timeout)
            }
        }
    }
    suspend fun getPlan(planCode: String, sentryHeader: String?, tvAppId: String?): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = this@SubscriptionHttpClient.host
                    path("$subsPath/plans/$planCode")
                }
                setApiKeyHeader()
                setSentryHeader(sentryHeader)
                setTvAppId(tvAppId)
                applyAppConfig(appConfig)
                applyTimeout(timeout)
            }
        }
    }
    suspend fun getPlanUpgrades(authToken: String, planCode: String, sentryHeader: String?, tvAppId: String?): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = this@SubscriptionHttpClient.host
                    path("$subsPath/accounts/plans/$planCode/upgrades")
                }
                setApiKeyHeader()
                setSentryHeader(sentryHeader)
                setTvAppId(tvAppId)
                applyUserBearerToken(authToken)
                applyAppConfig(appConfig)
                applyTimeout(timeout)
            }
        }
    }
    suspend fun getVersion(): HttpResult<HttpResponse> {
        return safeHttpCall {
            httpClient.get {
                url {
                    protocol = URLProtocol.HTTPS
                    host = this@SubscriptionHttpClient.host
                    path("$subsPath/version")
                }
                setApiKeyHeader()
                applyAppConfig(appConfig)
                applyTimeout(timeout)
            }
        }
    }
    private fun HttpRequestBuilder.setApiKeyHeader() {
        if (!isPartner) {
            subsApiKey?.let { header(SUBSCRIPTION_API_KEY, it) }
        }
    }

}
